import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { axiosErrorHandler } from 'common/helpers/axios.helper';
import { IUserShort } from 'common/models';
import { AppDispatch, RootState } from 'app/store';
import {
  IContract,
  ICreateContractPayload,
  ICreateContractUserPayload,
  IDeleteContractPayload,
  IDeleteContractUserPayload,
  IContractListParams,
  IUnsetContractManualSettingsPayload,
  IContractSyncStatusParams,
  IUpdateContractPayload,
  IContractUserListParams,
} from 'entities/Contract/Contract.models';
import { contractTransport } from 'entities/Contract/Contract.transport';

interface IState {
  contract: IContract | null;
  contractList: IContract[];
  contractCount: number;
  isContractLoading: boolean;
  contractUsers: IUserShort[];
}

const initialState: IState = {
  contract: null,
  contractList: [],
  contractCount: 0,
  isContractLoading: false,
  contractUsers: [],
};

export const contractSlice = createSlice({
  name: 'contract',
  initialState,
  reducers: {
    setContract(state, action: PayloadAction<IContract | null>) {
      state.contract = action.payload;
    },
    setContractList(state, action: PayloadAction<IContract[]>) {
      state.contractList = action.payload;
    },
    setContractCount(state, action: PayloadAction<number>) {
      state.contractCount = action.payload;
    },
    setIsContractLoading(state, action: PayloadAction<boolean>) {
      state.isContractLoading = action.payload;
    },
    setContractUsers(state, action: PayloadAction<IUserShort[]>) {
      state.contractUsers = action.payload;
    },
    addContractListItem(state, action: PayloadAction<IContract>) {
      state.contractList.push(action.payload);
    },
    patchContractListItem(state, action: PayloadAction<IContract>) {
      const contract = state.contractList.find((item) => item.id === action.payload.id);

      if (!contract) {
        return;
      }

      Object.assign(contract, action.payload);
    },
    removeContractListItem(state, action: PayloadAction<number>) {
      state.contractList = state.contractList.filter((item) => item.id !== action.payload);
    },
    addContractUsersItem(state, action: PayloadAction<IUserShort>) {
      state.contractUsers.push(action.payload);
    },
    removeContractUsersItem(state, action: PayloadAction<number>) {
      state.contractUsers = state.contractUsers.filter((item) => item.id !== action.payload);
    },
  },
});

export const {
  setContract,
  setContractList,
  setContractCount,
  setIsContractLoading,
  setContractUsers,
  addContractListItem,
  patchContractListItem,
  removeContractListItem,
  addContractUsersItem,
  removeContractUsersItem,
} = contractSlice.actions;

export const selectContract = (state: RootState) => state.contract.contract;
export const selectContractList = (state: RootState) => state.contract.contractList;
export const selectContractCount = (state: RootState) => state.contract.contractCount;
export const selectIsContractLoading = (state: RootState) => state.contract.isContractLoading;
export const selectContractUsers = (state: RootState) => state.contract.contractUsers;

export const contractReducer = contractSlice.reducer;

export const getContractList = (params: IContractListParams) => async (dispatch: AppDispatch) => {
  dispatch(setIsContractLoading(true));

  try {
    const response = await contractTransport.get(params);
    dispatch(setContractList(response.data));
    dispatch(setContractCount(response.count));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsContractLoading(false));
  }
};

export const getContractById = (id: number) => async (dispatch: AppDispatch) => {
  dispatch(setIsContractLoading(true));

  try {
    const response = await contractTransport.getById(id);
    dispatch(setContract(response));
    return response;
  } catch (error) {
    axiosErrorHandler(error);
    return null;
  } finally {
    dispatch(setIsContractLoading(false));
  }
};

export const createContract = (payload: ICreateContractPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsContractLoading(true));

  try {
    const response = await contractTransport.create(payload);
    dispatch(addContractListItem(response));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsContractLoading(false));
  }
};

export const updateContract = (payload: IUpdateContractPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsContractLoading(true));

  try {
    const response = await contractTransport.update(payload);
    dispatch(setContract(response));
    dispatch(patchContractListItem(response));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsContractLoading(false));
  }
};

export const deleteContract = (payload: IDeleteContractPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsContractLoading(true));

  try {
    const response = await contractTransport.delete(payload);
    dispatch(removeContractListItem(response.id));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsContractLoading(false));
  }
};

export const getContractUsers = (params: IContractUserListParams) => async (dispatch: AppDispatch) => {
  dispatch(setIsContractLoading(true));

  try {
    const response = await contractTransport.getUsers(params);
    dispatch(setContractUsers(response.data));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsContractLoading(false));
  }
};

export const createContractUser = (payload: ICreateContractUserPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsContractLoading(true));

  try {
    const response = await contractTransport.createUser(payload);
    dispatch(addContractUsersItem(response));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsContractLoading(false));
  }
};

export const deleteContractUser = (payload: IDeleteContractUserPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsContractLoading(true));

  try {
    const response = await contractTransport.deleteUser(payload);
    dispatch(removeContractUsersItem(response.id));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsContractLoading(false));
  }
};

export const getContractSyncStatus = (params: IContractSyncStatusParams) => async (dispatch: AppDispatch) => {
  dispatch(setIsContractLoading(true));

  try {
    return contractTransport.getSyncStatus(params);
  } catch (error) {
    axiosErrorHandler(error);
    return null;
  } finally {
    dispatch(setIsContractLoading(false));
  }
};

export const unsetContractManualSettings = (payload: IUnsetContractManualSettingsPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsContractLoading(true));

  try {
    const response = await contractTransport.unsetManualSettings(payload);
    dispatch(setContract(response));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsContractLoading(false));
  }
};
